import React, { useEffect, useState } from 'react';
import { authFetch } from '../auth';
import API_BASE_URL from '../config';

export default function AdminDashboard() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/reports`);
      setReports(await res.json());
    } catch { setErr('Failed loading reports'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await authFetch(`${API_BASE_URL}/api/reports/${id}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    });
    setReports(r => r.map(x => x._id === id ? { ...x, status } : x));
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="container mx-auto px-4 pb-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-display font-bold text-main">Admin Dashboard</h1>
        <p className="text-muted mt-1 mb-6">Manage and update status of reported issues.</p>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Total Issues" count={reports.length} color="bg-gray-500/10 text-gray-500 border-gray-500/20" />
          <StatCard label="Reported" count={reports.filter(r => r.status === 'Reported').length} color="bg-amber-500/10 text-amber-500 border-amber-500/20" />
          <StatCard label="In Progress" count={reports.filter(r => r.status === 'In Progress').length} color="bg-blue-500/10 text-blue-500 border-blue-500/20" />
          <StatCard label="Resolved" count={reports.filter(r => r.status === 'Resolved').length} color="bg-green-500/10 text-green-500 border-green-500/20" />
          <StatCard label="Rejected" count={reports.filter(r => r.status === 'Rejected').length} color="bg-red-500/10 text-red-500 border-red-500/20" />
        </div>
      </div>

      {err && <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 font-medium">{err}</div>}

      <div className="grid gap-4">
        {reports.map(r => (
          <div key={r._id} className="glass-panel p-5 rounded-xl flex flex-col md:flex-row gap-6 transition-all hover:border-primary-500/30">
            {/* Image Thumbnail */}
            <div className="shrink-0">
              {r.imageUrl ?
                <img src={r.imageUrl} alt="Evidence" className="w-full md:w-32 h-32 object-cover rounded-lg shadow-sm border border-white/10" />
                :
                <div className="w-full md:w-32 h-32 bg-dark-800/50 rounded-lg flex items-center justify-center text-muted border border-white/5">
                  <span className="text-xs">No Image</span>
                </div>
              }
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-bold text-lg text-main truncate pr-4">{r.description}</h3>
                <span className={`shrink-0 px-2.5 py-0.5 text-xs font-bold uppercase tracking-wider rounded border ${r.status === 'Resolved' ? 'bg-green-500/10 text-green-500 border-green-500/20' :
                  r.status === 'Rejected' ? 'bg-red-500/10 text-red-500 border-red-500/20' :
                    r.status === 'In Progress' ? 'bg-blue-500/10 text-blue-500 border-blue-500/20' :
                      'bg-amber-500/10 text-amber-500 border-amber-500/20'
                  }`}>
                  {r.status}
                </span>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-2 gap-x-4 text-sm text-muted mt-3">
                <div>
                  <span className="block text-xs opacity-60">Reported By</span>
                  <span className="font-medium text-main">{r.userName || 'Anonymous'}</span>
                </div>
                <div>
                  <span className="block text-xs opacity-60">Date</span>
                  <span>{new Date(r.timestamp).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="block text-xs opacity-60">Location</span>
                  <span className="font-mono">{r.location ? `${r.location.latitude.toFixed(4)}, ...` : 'N/A'}</span>
                </div>
                <div>
                  <span className="block text-xs opacity-60">ID</span>
                  <span className="font-mono text-xs opacity-80">{r._id.slice(-6)}</span>
                </div>
              </div>

              {/* Action Area */}
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-3">
                <label className="text-sm font-medium text-main mr-2">Update Status:</label>
                <select
                  value={r.status}
                  onChange={e => updateStatus(r._id, e.target.value)}
                  className="bg-dark-900/50 dark:bg-dark-800 border border-white/10 text-main text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500 transition-shadow"
                >
                  {['Reported', 'In Progress', 'Resolved', 'Rejected'].map(s => <option key={s} className="bg-dark-800">{s}</option>)}
                </select>
              </div>
            </div>
          </div>
        ))}
        {!reports.length && (
          <div className="text-center py-16 bg-white/5 rounded-2xl border border-dashed border-white/10">
            <p className="text-muted">No reports found.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, count, color }) {
  return (
    <div className={`p-4 rounded-xl border ${color} flex flex-col items-center justify-center`}>
      <span className="text-2xl font-bold">{count}</span>
      <span className="text-xs uppercase tracking-wider opacity-80">{label}</span>
    </div>
  );
}
